import {
  Button,
  TextField,
  Box,
  Grid,
  Paper,
  useMediaQuery,
  Autocomplete,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import useLicenseSelect from '../../hooks/selects/useLicenseSelect';
import PaymentOccurrenceSelect from '../../components/saas/PaymentOccurrenceSelect';
import tenantService from '../tenants/tenantService';

const statusOptions = [
  { value: 1, label: 'Aktív' },
  { value: 2, label: 'Inaktív' },
  { value: 3, label: 'Felfüggesztve' },
  { value: 4, label: 'Törölve' },
  { value: 5, label: 'Próbaidő' },
  { value: 6, label: 'Lejárt fizetés' },
  { value: 7, label: 'Lemondva' },
];

const SubscriptionForm = (props: any) => {
  const { entity, onSubmit, errors, handleCancel, readonly = false } = props;
  const { LicenseSelect } = useLicenseSelect();
  const [tenants, setTenants] = useState<any[]>([]);
  const isSmallScreen = useMediaQuery((theme: any) =>
    theme.breakpoints.down('sm')
  );

  useEffect(() => {
    tenantService.list().then((response) => {
      if (!response.hasError) setTenants(response.records);
    });
  }, []);

  return (
    <Formik
      initialValues={entity}
      enableReinitialize={true}
      validate={(values) => {
        const validation: any = {};
        if (!values.name) validation.name = 'Required';
        if (!values.tenantId) validation.tenantId = 'Required';
        if (!values.licenseId) validation.licenseId = 'Required';
        return validation;
      }}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values, setSubmitting);
      }}
    >
      {({
        isSubmitting,
        values,
        touched,
        setFieldValue,
        handleChange,
        errors: validationErrors,
      }) => (
        <Form>
          <Paper>
            <Grid container spacing={2} sx={{ justifyContent: 'left', p: 5 }}>
              <h2>{values.id ? 'Edit' : 'Create'} Subscription</h2>
              <Grid size={12} sx={{ pt: 2 }}>
                <Box sx={{ color: 'red' }}>
                  {(errors ?? []).map((error: string) => (
                    <li key={error}>{error}</li>
                  ))}
                </Box>
              </Grid>
              <Grid size={12}>
                <TextField
                  value={values.name ?? ''}
                  onChange={handleChange}
                  label="Name"
                  name="name"
                  required
                  disabled={readonly}
                  fullWidth
                  error={!!touched.name && !!validationErrors.name}
                  helperText={
                    !!touched.name &&
                    !!validationErrors.name &&
                    (validationErrors.name as string)
                  }
                />
              </Grid>
              <Grid size={12}>
                <Autocomplete
                  options={tenants}
                  getOptionLabel={(option: any) => option.name ?? ''}
                  value={tenants.find((t) => t.id === values.tenantId) ?? null}
                  disabled={readonly}
                  onChange={(_e, newValue: any) =>
                    setFieldValue('tenantId', newValue?.id ?? '')
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bérlő"
                      error={!!touched.tenantId && !!validationErrors.tenantId}
                      helperText={
                        !!touched.tenantId &&
                        !!validationErrors.tenantId &&
                        (validationErrors.tenantId as string)
                      }
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <LicenseSelect
                  value={values.licenseId ?? null}
                  onChange={(_event: any, newValue: any) =>
                    setFieldValue('licenseId', newValue)
                  }
                />
              </Grid>
              <Grid size={12}>
                <PaymentOccurrenceSelect
                  value={values.paymentOccurrence ?? 'Monthly'}
                  disabled={readonly}
                  onChange={(v) => setFieldValue('paymentOccurrence', v)}
                />
              </Grid>
              <Grid size={12}>
                <Autocomplete
                  options={statusOptions.map((x) => x.value)}
                  getOptionLabel={(value: any) =>
                    statusOptions.find((x) => x.value === value)?.label ?? ''
                  }
                  value={values.status ?? 1}
                  disabled={readonly}
                  onChange={(_e, newValue) => setFieldValue('status', newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Status" />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Starts at"
                  disabled={readonly}
                  value={
                    values.startsAt ? dayjs(String(values.startsAt).substring(0, 10)) : null
                  }
                  onChange={(d) =>
                    setFieldValue(
                      'startsAt',
                      d ? d.format('YYYY-MM-DD') : ''
                    )
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Ends at"
                  disabled={readonly}
                  value={
                    values.endsAt ? dayjs(String(values.endsAt).substring(0, 10)) : null
                  }
                  onChange={(d) =>
                    setFieldValue('endsAt', d ? d.format('YYYY-MM-DD') : '')
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid container sx={{ pt: 3, justifyContent: 'left' }}>
                {!readonly && (
                  <Grid
                    size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }}
                    sx={{ p: 1 }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth={isSmallScreen}
                      disabled={isSubmitting}
                    >
                      Mentés
                    </Button>
                  </Grid>
                )}
                <Grid
                  size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }}
                  sx={{ p: 1 }}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    fullWidth={isSmallScreen}
                    onClick={handleCancel}
                  >
                    Mégse
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Form>
      )}
    </Formik>
  );
};

export default SubscriptionForm;
